import { useRef, useCallback } from 'react';
import { Node } from 'reactflow';

const MAX_SNAPSHOTS = 10;

/**
 * Single-purpose layout undo history.
 * Completely separate from the workflow undo/redo system.
 * Stores up to 10 node position snapshots, one per layout run.
 */
export function useLayoutHistory(
  setNodes: (updater: Node[] | ((nds: Node[]) => Node[])) => void
): {
  pushLayoutSnapshot: (nodes: Node[]) => void;
  undoLayout: () => void;
  canUndoLayout: boolean;
} {
  // Ring buffer of position snapshots
  const snapshotsRef = useRef<Node[][]>([]);
  // Force re-render when stack changes — we use a simple counter ref + callback pattern
  const canUndoRef = useRef(false);

  const pushLayoutSnapshot = useCallback((nodes: Node[]) => {
    // Deep-clone positions only — we don't need full node data
    const snapshot = nodes.map(n => ({
      ...n,
      position: { x: n.position.x, y: n.position.y },
      style: { ...n.style, transition: undefined }, // strip transition from snapshot
    }));

    snapshotsRef.current = [
      ...snapshotsRef.current.slice(-(MAX_SNAPSHOTS - 1)),
      snapshot,
    ];
    canUndoRef.current = snapshotsRef.current.length > 0;

    console.log('📸 LAYOUT-HISTORY: Snapshot pushed, stack depth:', snapshotsRef.current.length);
  }, []);

  const undoLayout = useCallback(() => {
    if (snapshotsRef.current.length === 0) return;

    const snapshots = [...snapshotsRef.current];
    const previous = snapshots.pop()!;
    snapshotsRef.current = snapshots;
    canUndoRef.current = snapshots.length > 0;

    console.log('↶ LAYOUT-HISTORY: Restoring snapshot, remaining stack:', snapshots.length);

    // Restore positions with a smooth transition
    setNodes(currentNodes =>
      currentNodes.map(node => {
        const snap = previous.find(s => s.id === node.id);
        if (!snap) return node;
        return {
          ...node,
          position: snap.position,
          style: {
            ...node.style,
            transition: 'transform 400ms cubic-bezier(0.4, 0, 0.2, 1)',
          },
        };
      })
    );
  }, [setNodes]);

  return {
    pushLayoutSnapshot,
    undoLayout,
    get canUndoLayout() {
      return snapshotsRef.current.length > 0;
    },
  };
}
